import { MutationType, ShapeType, TargetType } from '@tencent/slide-sdk-pb-model';
import { DataTypeUtils, DataType } from '../data/data';
import { forEachDescendantShapes } from '../data/shape-data';
import { TextDataUtils } from '../mutation/text/set-text-data-mutation';
import { TextBodyPropertiesUtils } from '../mutation/text/set-text-body-properties-mutation';
import { shallowOmit } from '../utils/object-utils';
import { clone } from '../utils/protoutil';
import { ShapeDeltaUtils } from '../mutation/base/shape-delta-utils';
import { getTableKeyframe } from './get-table-keyframe';
import { getChartKeyframe } from './get-chart-keyframe';
import { getSmartArtKeyframe } from './get-smart-art-keyframe';
import { getDataExtensionKeyframe } from './get-data-extension-keyframe';
import type { KeyframeContext } from './keyframe-context.interface';
import type { MarkRequired } from 'ts-essentials';
import type {
    IValidMutationData,
    IValidTransformShapeMutationData,
    IValidGroupMutationData,
    IValidAddShapeMutationData,
    IValidSetShapePropertiesMutationData,
} from '../mutation/base';
import type { IDrawingShapeData } from '../data/shape-data';

const defaultParagraph = [
    {
        egTextRun: [],
        endParaRPr: {},
        pPr: {},
    },
];

export function getShapeKeyframe(
    shape: IDrawingShapeData,
    context: MarkRequired<KeyframeContext, 'page'>,
    shapeTreeId: string,
    startIndex: number,
): IValidMutationData[] {
    const mutations: IValidMutationData[] = [];

    forEachDescendantShapes(
        { shapeList: [shape], id: shapeTreeId },
        (child, offset, parent, parentOffset) => {
            getSingleShapeKeyFrame(child, context, shapeTreeId, parentOffset + offset).forEach((mutation) =>
                mutations.push(mutation),
            );
        },
        startIndex,
    );
    return mutations;
}

// eslint-disable-next-line complexity
export function getSingleShapeKeyFrame(
    shape: IDrawingShapeData,
    context: MarkRequired<KeyframeContext, 'page'>,
    parentId: string,
    insertIndex = 0,
): IValidMutationData[] {
    const pageId = context.idFactory(context.page.id);
    const shapeId = context.idFactory(shape.id);

    const isEquation = shape.type === DataType.Shape && shape.nvPr.isEquationShape;

    const transformShapeMuts: IValidTransformShapeMutationData[] = shape.spPr.xfrm
        ? [
              {
                  type: MutationType.TransformShape,
                  pageId,
                  shapeId,
                  parentId,
                  shapeType: isEquation ? ShapeType.Equation : DataTypeUtils.toShapeType(shape.type),
                  xfrmDelta: shallowOmit(shape.spPr.xfrm, 'chOff', 'chExt'),
              },
          ]
        : [];

    if (shape.type === DataType.GroupShape) {
        if (transformShapeMuts[0]) {
            transformShapeMuts[0].chXfrmDelta = shape.spPr.xfrm && {
                chOff: shape.spPr.xfrm.chOff,
                chExt: shape.spPr.xfrm.chExt,
            };
        }

        const group = {
            type: MutationType.Group,
            pageId,
            groupShapeId: shapeId,
            shapeIds: shape.shapeList.map((shape) => context.idFactory(shape.id)),
            parentId,
            index: insertIndex,
            isSmartArt: false,
        } satisfies IValidGroupMutationData;

        const setGroupProperties = {
            type: MutationType.SetShapeProperties,
            pageId,
            shapeId,
            shapeDelta: clone(ShapeDeltaUtils.fromGroupShapeData(shape)),
        } satisfies IValidSetShapePropertiesMutationData;

        return [group, setGroupProperties, ...transformShapeMuts];
    }

    if (shape.type === DataType.Connector) {
        const { nvPr } = shape;
        if (nvPr.stCxn?.id) {
            nvPr.stCxn.id = context.idFactory(nvPr.stCxn.id);
        }
        if (nvPr.endCxn?.id) {
            nvPr.endCxn.id = context.idFactory(nvPr.endCxn.id);
        }
    }

    const addShape: IValidAddShapeMutationData = {
        type: MutationType.AddShape,
        pageId,
        shapeId,
        shapeType: isEquation ? ShapeType.Equation : DataTypeUtils.toShapeType(shape.type),
        tableOptions: {},
        parentId,
        index: insertIndex,
    } satisfies IValidAddShapeMutationData;
    let setTableDataMuts: IValidMutationData[] = [];
    if (shape.type === DataType.GraphicalObjectFrameTable) {
        addShape.tableOptions = {
            rowCount: shape.graphic?.graphicData?.tbl?.tr?.length || 1,
            colCount: shape.graphic?.graphicData?.tbl?.tblGrid?.gridCol?.length || 1,
        };
        setTableDataMuts = getTableKeyframe(shape, context);
    }

    let setChartDataMuts: IValidMutationData[] = [];
    if (shape.type === DataType.GraphicalObjectFrameChart) {
        setChartDataMuts = getChartKeyframe(shape, context);
    }

    let setSmartArtDataMuts: IValidMutationData[] = [];
    if (shape.type === DataType.GraphicalObjectFrameSmartArt) {
        setSmartArtDataMuts = getSmartArtKeyframe(shape, context);
    }

    const setTextDataMuts: IValidMutationData[] = getShapeTextKeyFrame(shape, pageId, shapeId);

    const setShapeProperties = {
        type: MutationType.SetShapeProperties,
        pageId,
        shapeId,
        shapeDelta: clone(ShapeDeltaUtils.fromDrawingShapeData(shape)),
    } satisfies IValidSetShapePropertiesMutationData;

    return [
        addShape,
        setShapeProperties,
        ...transformShapeMuts,
        ...setTableDataMuts,
        ...setTextDataMuts,
        ...setChartDataMuts,
        ...setSmartArtDataMuts,
        ...getDataExtensionKeyframe(TargetType.Shape, shape, pageId, shapeId),
    ];
}

function getShapeTextKeyFrame(child: IDrawingShapeData, pageId: string, shapeId: string) {
    const setTextDataMuts: IValidMutationData[] = [];
    const setTextBodyPropertiesMuts: IValidMutationData[] = [];
    if (child.type === DataType.Shape && child.txBody) {
        const { bodyPr, lstStyle, p } = child.txBody;
        if (bodyPr || lstStyle) {
            setTextBodyPropertiesMuts.push(
                TextBodyPropertiesUtils.fromTextBodyProperties(child.txBody, pageId, shapeId),
            );
        }
        if (p?.length) {
            setTextDataMuts.push(TextDataUtils.fromParagraphListData(p, pageId, shapeId));
        } else {
            // p = [] 时补充一个默认段落，防止出现 textBody 里 p 为空的情况
            setTextDataMuts.push(TextDataUtils.fromParagraphListData(defaultParagraph, pageId, shapeId));
        }
    } else if (child.type === DataType.GraphicalObjectFrameTable) {
        child.graphic?.graphicData?.tbl?.tr?.forEach((row, rowIndex) =>
            row?.tc?.forEach((col, colIndex) => {
                if (!col.txBody) return;
                const { bodyPr, lstStyle, p } = col.txBody;
                if (bodyPr || lstStyle) {
                    setTextBodyPropertiesMuts.push(
                        TextBodyPropertiesUtils.fromTextBodyProperties(col.txBody, pageId, shapeId, colIndex, rowIndex),
                    );
                }
                if (p?.length) {
                    setTextDataMuts.push(TextDataUtils.fromParagraphListData(p, pageId, shapeId, colIndex, rowIndex));
                }
            }),
        );
    }
    return [...setTextDataMuts, ...setTextBodyPropertiesMuts];
}
